import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { DefinePluginOptions } from '../types/define';
import { createTreeApi } from './tree';
import type {
  PluginHooks,
  StoreType,
  PostRenderFn,
  BootstrapParams,
  BootstrapFn,
  ShouldRenderFn,
  PostRenderParams,
  LogType,
  FieldSuggestionsFn,
  PreRenderFn,
  PreRenderParams,
  FieldSuggestionsParams,
  PostFieldSuggestionsBindParams,
  Setting,
  SettingDef,
  SettingValues,
} from '../types';
import { SchemaVariant, TreeNode } from '@morph-mapper/types';
import { useGlobalPluginStore } from '../store';
import { createMapApi } from './map';
import { createUtilsApi } from './utils';
import { usePluginCtx } from '../context';

/**
 * This function is the external API for defining a plugin and should be the main point of entry for plugin authors.
 */
export function definePlugin<
  S,
  T extends Record<string, SettingDef> = Record<string, SettingDef>,
>(opts: DefinePluginOptions<S, T>): () => PluginHooks {
  // Create a Zustand store with Immer middleware
  const usePluginStore = create(
    immer<StoreType<S> & { domain?: string; fileType?: SchemaVariant }>(
      (set) => ({
        slice: {
          ...opts.initialState,
          domain: undefined,
          fileType: undefined,
        },
        setState: (updater) =>
          set((draft) => {
            updater(draft.slice as any);
          }),
      }),
    ),
  );

  // If a plugin does not have one of the handlers defined, we use a noop.
  const noop = () => {};

  const getPluginContext = () => {
    const state = usePluginStore.getState().slice;

    const setState = (updater: (draft: any) => void) => {
      usePluginStore.setState((draft) => {
        updater(draft.slice as any);
      });
    };

    return { state, setState };
  };

  return function usePlugin() {
    const { createCallRender, createLLMExtract } = usePluginCtx();
    // Subscribe to the slice only, as this is where the plugin state is stored.
    const name = opts.name || 'unknown'; // or a dedicated plugin name
    // Wrap handlers to inject application and plugin context.;

    const defaultSettings: Setting[] = opts.settings
      ? Object.entries(opts.settings).map(
          ([key, def]) => ({ ...def, key, value: def.default }) as Setting,
        )
      : [];

    const settingValues = (settings: Record<string, Setting>) => {
      return Object.fromEntries(
        Object.entries(settings).map(([k, s]) => [k, s.value]),
      ) as SettingValues<T>;
    };

    const shouldRender = opts.shouldRender
      ? (entry: TreeNode, settings: Record<string, Setting>) => {
          const { state, setState } = getPluginContext();
          return opts.shouldRender!({
            state,
            setState,
            entry,
            settings: settingValues(settings),
          });
        }
      : (noop as ShouldRenderFn);

    const postRender = opts.postRender
      ? (params: PostRenderParams, settings: Record<string, Setting>) => {
          const { state, setState } = getPluginContext();
          return opts.postRender!({
            state,
            setState,
            ...params,
            settings: settingValues(settings),
          });
        }
      : ((({ template }) => template) as PostRenderFn);

    const preRender = opts.preRender
      ? (params: PreRenderParams, settings: Record<string, Setting>) => {
          const { state, setState } = getPluginContext();
          const log = (message: string, type?: LogType) =>
            useGlobalPluginStore.getState().log(name, message, type);
          const progress = (value: number, message?: string) =>
            useGlobalPluginStore.getState().setProgress(name, value, message);
          useGlobalPluginStore.getState().setSkipFile(false);
          const skipSameFile = (state?: boolean) =>
            useGlobalPluginStore.getState().setSkipFile(state);
          return opts.preRender!({
            state,
            setState,
            log,
            progress,
            skipSameFile,
            ...createTreeApi(params.entries),
            ...createMapApi(params.entries),
            ...createUtilsApi(
              params.textContent,
              createCallRender,
              createLLMExtract,
            ),
            settings: settingValues(settings),
            textContent: params.textContent,
          });
        }
      : (noop as PreRenderFn);

    const bootstrap = opts.bootstrap
      ? (params: BootstrapParams, settings: Record<string, Setting>) => {
          const { state, setState } = getPluginContext();
          const log = (message: string, type?: LogType) =>
            useGlobalPluginStore.getState().log(name, message, type);
          const progress = (value: number, message?: string) =>
            useGlobalPluginStore.getState().setProgress(name, value, message);
          return opts.bootstrap!({
            state,
            setState,
            log,
            progress,
            ...createTreeApi(params.entries),
            ...createMapApi(params.entries),
            ...createUtilsApi(
              params.textContent,
              createCallRender,
              createLLMExtract,
            ),
            settings: settingValues(settings),
            textContent: params.textContent,
          });
        }
      : (noop as BootstrapFn);

    const fieldSuggestions = opts.fieldSuggestions
      ? (params: FieldSuggestionsParams, settings: Record<string, Setting>) => {
          const { state, setState } = getPluginContext();
          return opts.fieldSuggestions!({
            state,
            setState,
            ...params,
            ...createUtilsApi(
              useGlobalPluginStore.getState().textContent,
              createCallRender,
              createLLMExtract,
            ),
            settings: settingValues(settings),
          });
        }
      : ((() => []) as FieldSuggestionsFn);

    const postFieldSuggestions = opts.postFieldSuggestions
      ? (
          params: PostFieldSuggestionsBindParams,
          settings: Record<string, Setting>,
        ) => {
          const { state, setState } = getPluginContext();
          const postFields = opts.postFieldSuggestions!({
            state,
            setState,
            ...params,
            ...createUtilsApi(
              useGlobalPluginStore.getState().textContent,
              createCallRender,
              createLLMExtract,
            ),
            settings: settingValues(settings),
          });
          if (postFields == undefined) {
            return params.suggestions;
          }
          return postFields;
        }
      : ((() => []) as FieldSuggestionsFn);

    return {
      shouldRender,
      preRender,
      postRender,
      bootstrap,
      fieldSuggestions,
      postFieldSuggestions,
      _store: usePluginStore,
      version: opts.version,
      name,
      description: opts.description,
      settings: defaultSettings,
    };
  };
}
